import { Badge, Button, IconButton, Input, Prompt, Text, Textarea, toast } from "@medusajs/ui"
import { XMark } from "@medusajs/icons"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { FilePicker } from "@/components/file-picker"
import { RichTextEditor } from "@/components/rich-text"
import { DataTable, EmptyState, ErrorState, PageHeader, SaveBar, SearchInput, Section, Thumb, usePage } from "@/components/ui"
import { slugify } from "@/lib/catalog"
import { formatDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"

type Post = {
  id: string; title: string; handle: string; author: string; excerpt: string; body_html: string; image: string; tags: string[]
  seo_title: string | null; seo_description: string | null; published: boolean; published_at: string | null; updated_at: string
}
const storeUrl = () => (location.hostname === "localhost" ? "http://localhost:8000" : `https://${location.hostname.replace(/^(keel|terify)\./, "")}`)
const Pill = ({ on }: { on: boolean }) => <span className="tf-pill" style={on ? { background: "#affebf", color: "#014b40" } : { background: "#e3e3e3", color: "#616161" }}>{on ? "Visible" : "Hidden"}</span>

export function BlogPostsPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const navigate = useNavigate()
  const can = useCan()
  const { data, isLoading, error } = useQuery({
    queryKey: ["content", "posts", q, pageIndex],
    placeholderData: keepPreviousData,
    queryFn: () => sdk.client.fetch<{ posts: Post[]; count: number }>("/admin/terify/content/posts", { query: { q: q || undefined, limit: 50, offset: pageIndex * 50 } }),
  })
  const add = can("content:write") && <Button size="small" asChild><Link to="/content/blog/new">Add blog post</Link></Button>
  return (
    <>
      <PageHeader title="Blog posts" actions={add} />
      <Section>
        <div className="px-4 py-3"><SearchInput value={q} onChange={setQ} placeholder="Search posts" /></div>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable<Post>
            rowKey={(p) => p.id}
            rows={data?.posts}
            count={data?.count ?? 0}
            pageSize={50}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(p) => navigate(`/content/blog/${p.id}`)}
            empty={<EmptyState title="Create blog posts to educate your customers" body="Share news, guides and updates. Posts appear at /blogs/news on your store." action={add} />}
            columns={[
              { key: "title", header: "Title", cell: (p) => <div className="flex items-center gap-3"><Thumb src={p.image} size={32} /><span className="text-[13px] font-medium">{p.title}</span></div> },
              { key: "author", header: "Author", cell: (p) => p.author || "—" },
              { key: "tags", header: "Tags", cell: (p) => <div className="flex flex-wrap gap-1">{p.tags.slice(0, 3).map((t) => <Badge key={t} size="2xsmall">{t}</Badge>)}</div> },
              { key: "vis", header: "Visibility", cell: (p) => <Pill on={p.published} /> },
              { key: "updated", header: "Updated", cell: (p) => formatDate(p.updated_at, false) },
            ]}
            mobile={(p) => (
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0"><Text size="small" weight="plus" className="truncate">{p.title}</Text><Text size="xsmall" className="text-ui-fg-subtle">{p.author || "No author"} · {formatDate(p.updated_at, false)}</Text></div>
                <Pill on={p.published} />
              </div>
            )}
          />
        )}
      </Section>
    </>
  )
}

export function BlogPostEditorPage() {
  const { id: routeId } = useParams()
  const id = !routeId || routeId === "new" ? null : routeId
  const { data, error, isLoading } = useQuery({ queryKey: ["content", "post", id], enabled: !!id, queryFn: () => sdk.client.fetch<{ post: Post }>(`/admin/terify/content/posts/${id}`) })
  if (error) return <ErrorState error={error} />
  if (id && (isLoading || !data)) return null
  return <PostEditor key={id ?? "new"} post={data?.post ?? null} />
}

function PostEditor({ post }: { post: Post | null }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const canWrite = useCan()("content:write")
  const base = useMemo(
    () => ({ title: post?.title ?? "", handle: post?.handle ?? "", author: post?.author ?? "", excerpt: post?.excerpt ?? "", body_html: post?.body_html ?? "", image: post?.image ?? "", tags: post?.tags ?? ([] as string[]), seo_title: post?.seo_title ?? "", seo_description: post?.seo_description ?? "", published: post?.published ?? false }),
    [post],
  )
  const [d, setD] = useState(base)
  const [tag, setTag] = useState("")
  const [handleTouched, setHandleTouched] = useState(!!post)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [pickingImage, setPickingImage] = useState(false)
  useEffect(() => setD(base), [base])
  const dirty = JSON.stringify(d) !== JSON.stringify(base)
  const imageBad = d.image !== "" && !/^(https:\/\/|\/(?!\/))/i.test(d.image)
  const save = useMutation({
    mutationFn: () => {
      const body = { ...d, title: d.title.trim(), handle: d.handle.trim() || slugify(d.title), author: d.author.trim(), seo_title: d.seo_title.trim() || null, seo_description: d.seo_description.trim() || null }
      return sdk.client.fetch<{ post: Post }>(post ? `/admin/terify/content/posts/${post.id}` : "/admin/terify/content/posts", { method: "POST", body })
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["content"] })
      toast.success(post ? "Post saved" : "Post created")
      if (!post) navigate(`/content/blog/${r.post.id}`, { replace: true })
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const del = useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/content/posts/${post!.id}`, { method: "DELETE" }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ["content"] }), toast.success("Post deleted"), setD(base), navigate("/content/blog", { replace: true })),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !save.isPending && currentLocation.pathname !== nextLocation.pathname)
  const addTag = () => {
    const t = tag.trim().slice(0, 40)
    if (t && !d.tags.some((x) => x.toLowerCase() === t.toLowerCase()) && d.tags.length < 30) setD({ ...d, tags: [...d.tags, t] })
    setTag("")
  }
  const url = `${storeUrl()}/blogs/news/${d.handle || slugify(d.title) || "handle"}`

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!d.title.trim()) return toast.error("Title can't be blank.")
        if (imageBad) return toast.error("Use an https:// image link or a /path.")
        save.mutate()
      }}
      className="pb-24"
    >
      <PageHeader
        back="/content/blog"
        title={post?.title || "Add blog post"}
        actions={post && (
          <>
            {post.published && <Button type="button" size="small" variant="secondary" asChild><a href={url} target="_blank" rel="noreferrer">View post</a></Button>}
            {canWrite && <Button type="button" size="small" variant="secondary" onClick={() => setConfirmDelete(true)}>Delete</Button>}
          </>
        )}
      />
      <fieldset disabled={!canWrite} className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Section>
            <div className="flex flex-col gap-y-4 px-4 py-3">
              <Field label="Title"><Input value={d.title} maxLength={200} onChange={(e) => { const title = e.target.value; setD((x) => ({ ...x, title, handle: handleTouched ? x.handle : slugify(title) })) }} /></Field>
              <div className="flex flex-col gap-y-1.5">
                <Text size="small" weight="plus">Content</Text>
                <RichTextEditor label="Content" value={d.body_html} onChange={(body_html) => setD((x) => ({ ...x, body_html }))} minHeight={320} />
              </div>
              <Field label="Excerpt" hint="Shown on the blog index and in search results when there's no meta description."><Textarea rows={3} value={d.excerpt} maxLength={600} onChange={(e) => setD({ ...d, excerpt: e.target.value })} /></Field>
            </div>
          </Section>
          <Section title="Search engine listing">
            <div className="flex flex-col gap-y-4 px-4 py-3">
              <div className="rounded-lg border bg-ui-bg-subtle p-3">
                <Text size="small" className="text-ui-fg-interactive">{d.seo_title || d.title || "Post title"}</Text>
                <Text size="xsmall" className="text-ui-fg-muted">{url}</Text>
                <Text size="xsmall" className="mt-1 text-ui-fg-subtle">{d.seo_description || d.excerpt || d.body_html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160) || "Add a description to see how this post might appear in search results."}</Text>
              </div>
              <Field label="Page title" hint={`${d.seo_title.length} of 70 characters used`}><Input value={d.seo_title} maxLength={120} onChange={(e) => setD({ ...d, seo_title: e.target.value })} /></Field>
              <Field label="Meta description" hint={`${d.seo_description.length} of 160 characters used`}><Textarea rows={3} value={d.seo_description} maxLength={320} onChange={(e) => setD({ ...d, seo_description: e.target.value })} /></Field>
              <Field label="URL handle"><Input value={d.handle} onChange={(e) => { setHandleTouched(true); setD({ ...d, handle: slugify(e.target.value) || e.target.value.toLowerCase() }) }} /></Field>
            </div>
          </Section>
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <Section title="Visibility">
            <div className="flex flex-col gap-y-2 px-4 py-3">
              {[true, false].map((v) => (
                <label key={String(v)} className="flex items-center gap-x-2">
                  <input type="radio" name="visibility" checked={d.published === v} onChange={() => setD({ ...d, published: v })} className="accent-[#303030]" />
                  <Text size="small">{v ? "Visible" : "Hidden"}</Text>
                </label>
              ))}
              {post?.published_at && d.published && <Text size="xsmall" className="text-ui-fg-muted">Published {formatDate(post.published_at)}</Text>}
            </div>
          </Section>
          <Section title="Image">
            <div className="flex flex-col gap-3 px-4 py-3">
              <Field label="Image URL" hint={imageBad ? "Use an https:// link or a /path" : "Paste a link, or choose a file from your library."}><Input value={d.image} maxLength={500} placeholder="https://…" aria-invalid={imageBad} onChange={(e) => setD({ ...d, image: e.target.value.trim() })} /></Field>
              {d.image && !imageBad && <img src={d.image} alt="" className="max-h-40 w-full rounded-lg border object-cover" />}
              {canWrite && <div><Button type="button" size="small" variant="secondary" onClick={() => setPickingImage(true)}>Select from Files</Button></div>}
              {pickingImage && <FilePicker title="Select image" onClose={() => setPickingImage(false)} onDone={([f]) => { setPickingImage(false); setD({ ...d, image: f.url }) }} />}
            </div>
          </Section>
          <Section title="Organization">
            <div className="flex flex-col gap-3 px-4 py-3">
              <Field label="Author"><Input value={d.author} maxLength={120} onChange={(e) => setD({ ...d, author: e.target.value })} /></Field>
              <Field label="Tags" hint="Press Enter to add">
                <Input value={tag} maxLength={40} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag() } }} onBlur={addTag} />
              </Field>
              <div className="flex flex-wrap gap-1.5">
                {d.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-ui-bg-subtle-hover px-2 py-0.5 text-[12px]">
                    {t}
                    {canWrite && <IconButton type="button" size="2xsmall" variant="transparent" aria-label={`Remove tag ${t}`} onClick={() => setD({ ...d, tags: d.tags.filter((x) => x !== t) })}><XMark /></IconButton>}
                  </span>
                ))}
              </div>
            </div>
          </Section>
        </div>
      </fieldset>
      {canWrite && <SaveBar visible={dirty || !post} message={post ? "Unsaved changes" : "Unsaved post"} saving={save.isPending} onDiscard={() => (post ? setD(base) : navigate("/content/blog"))} />}
      <Prompt open={blocker.state === "blocked"}>
        <Prompt.Content>
          <Prompt.Header><Prompt.Title>Leave page with unsaved changes?</Prompt.Title><Prompt.Description>Leaving this page will discard all unsaved changes.</Prompt.Description></Prompt.Header>
          <Prompt.Footer><Prompt.Cancel onClick={() => blocker.reset?.()}>Stay</Prompt.Cancel><Prompt.Action onClick={() => blocker.proceed?.()}>Leave page</Prompt.Action></Prompt.Footer>
        </Prompt.Content>
      </Prompt>
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header><Prompt.Title>Delete {post?.title}?</Prompt.Title><Prompt.Description>Links to this post will stop working.</Prompt.Description></Prompt.Header>
          <Prompt.Footer><Prompt.Cancel>Cancel</Prompt.Cancel><Prompt.Action onClick={() => del.mutate()}>Delete post</Prompt.Action></Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  )
}
